import { useState, useEffect, useCallback } from 'react';
import { generateDemoData, WeightDataPoint } from '../utils/weightDemoData';

export type DateRange = '7d' | '30d' | '90d' | 'all';

interface WeightStats {
    min: number;
    max: number;
    avg: number;
    latest: number;
    change: number;
    count: number;
}

interface AppleHealthResponse {
    count?: number;
    data?: Array<{
        date: string;
        weight?: number;
    }>;
    message?: string;
    success?: boolean;
}

const API_BASE = 'https://ai-reply-bot.vercel.app/api/health-api';
const ACTIVE_PROFILE_URL = 'https://ai-reply-bot.vercel.app/api/active-profile';
const API_KEY = import.meta.env.VITE_HEALTH_API_KEY || import.meta.env.VITE_APPLE_HEALTH_API_KEY || '';
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes (mirrors API's cache TTL)
// Bump version to avoid serving previously-shifted cached dates
const CACHE_STORAGE_KEY_PREFIX = 'health-api:weight:v3:'; // v3 — includes profileId in key
const DEFAULT_PROFILE = 'default';

// IMPORTANT: `new Date("YYYY-MM-DD")` is parsed as UTC by JS.
// We treat these date keys as "calendar days" and parse them as local-midnight
// to avoid off-by-one display bugs in local timezones.
const parseLocalYYYYMMDD = (dateStr: string): Date => {
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) return new Date(dateStr);
    return new Date(y, m - 1, d);
};

// NOTE: The API returns/stores `date` as a *date key* (YYYY-MM-DD), not a timestamp.
// We should NOT convert that "UTC day" into a different local day here, otherwise users
// in negative offsets (e.g. CST) will see entries shifted back by 1 day.

type WeightCachePayload = {
    cachedAt: number;
    data: WeightDataPoint[];
};

const buildCacheKey = (params: URLSearchParams) => `${CACHE_STORAGE_KEY_PREFIX}${params.toString()}`;

const readCache = (key: string): WeightCachePayload | null => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as WeightCachePayload;
        if (!parsed || typeof parsed.cachedAt !== 'number' || !Array.isArray(parsed.data)) return null;
        return parsed;
    } catch {
        return null;
    }
};

const writeCache = (key: string, payload: WeightCachePayload) => {
    try {
        localStorage.setItem(key, JSON.stringify(payload));
    } catch {
        // ignore storage quota / privacy mode errors
    }
};

const getDateRangeFilter = (range: DateRange) => {
    if (range === 'all') return null;

    const endDate = new Date();
    const startDate = new Date();

    const daysBack = {
        '7d': 7,
        '30d': 30,
        '90d': 90,
    }[range];

    startDate.setDate(endDate.getDate() - daysBack);

    return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
    };
};

const filterDataByRange = (data: WeightDataPoint[], range: DateRange): WeightDataPoint[] => {
    const filter = getDateRangeFilter(range);
    if (!filter) return data;

    const start = parseLocalYYYYMMDD(filter.startDate);
    const end = parseLocalYYYYMMDD(filter.endDate);

    return data.filter(item => {
        const date = parseLocalYYYYMMDD(item.date);
        return date >= start && date <= end;
    });
};

const calculateStats = (data: WeightDataPoint[]): WeightStats | null => {
    if (data.length === 0) return null;

    const weights = data.map(d => d.weight);
    const min = Math.min(...weights);
    const max = Math.max(...weights);
    const avg = weights.reduce((sum, w) => sum + w, 0) / weights.length;
    const latest = data[data.length - 1].weight;
    const change = data.length > 1 ? latest - data[0].weight : 0;

    return { min, max, avg, latest, change, count: data.length };
};

const fetchDemoData = async (range: DateRange): Promise<WeightDataPoint[]> => {
    await new Promise(resolve => setTimeout(resolve, 800));
    const allData = generateDemoData(100);
    return filterDataByRange(allData, range);
};

const fetchAPIData = async (range: DateRange, profileId: string): Promise<{ data: WeightDataPoint[]; cacheKey: string }> => {
    const params = new URLSearchParams({
        metrics: 'weight',
        sort: 'asc',
        profileId,
    });

    const filter = getDateRangeFilter(range);
    if (filter) {
        params.set('startDate', filter.startDate);
        params.set('endDate', filter.endDate);
    }

    const cacheKey = buildCacheKey(params);

    const response = await fetch(`${API_BASE}?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${API_KEY}`,
            'Content-Type': 'application/json'
        },
    });

    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
        await response.text();
        throw new Error(`API returned non-JSON response. Status: ${response.status}`);
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data: AppleHealthResponse = await response.json();

    if (data.success === false) {
        throw new Error(data.message || 'Failed to fetch weight data');
    }

    if (!data.data || !Array.isArray(data.data)) {
        return { data: [], cacheKey };
    }

    const normalized = data.data
        .filter(item => item.weight !== undefined && item.weight !== null)
        .map(item => ({
            date: item.date,
            weight: item.weight!,
        }))
        .sort((a, b) => parseLocalYYYYMMDD(a.date).getTime() - parseLocalYYYYMMDD(b.date).getTime());

    return { data: normalized, cacheKey };
};

// Persist chosen profile in localStorage so it survives page refreshes
const loadStoredProfile = (): string => {
    try {
        return localStorage.getItem('health:activeProfile') || DEFAULT_PROFILE;
    } catch {
        return DEFAULT_PROFILE;
    }
};

const saveStoredProfile = (profileId: string) => {
    try {
        localStorage.setItem('health:activeProfile', profileId);
    } catch { /* ignore */ }
};

// ── Profile management API calls ─────────────────────────────────────────────

export const fetchProfiles = async (): Promise<string[]> => {
    const res = await fetch(`${API_BASE}?action=profiles`, {
        headers: { 'Authorization': `Bearer ${API_KEY}` },
    });
    if (!res.ok) throw new Error(`Failed to fetch profiles: ${res.status}`);
    const data = await res.json();
    return Array.isArray(data.profiles) ? data.profiles : [];
};

export const createProfile = async (profileId: string): Promise<void> => {
    // Uses the dedicated action=create endpoint which writes an empty []
    // to S3 — no fake data entries are created.
    const res = await fetch(
        `${API_BASE}?action=create&profileId=${encodeURIComponent(profileId)}`,
        { headers: { 'Authorization': `Bearer ${API_KEY}` } }
    );
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to create profile: ${res.status}`);
    }
};

export const deleteProfile = async (profileId: string): Promise<void> => {
    const res = await fetch(
        `${API_BASE}?profileId=${encodeURIComponent(profileId)}&deleteProfile=true&confirmation=true`,
        {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${API_KEY}` },
        }
    );
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to delete profile: ${res.status}`);
    }
};

const PHANTOM_PROFILE_ID = 'phantom';
const PHANTOM_STORAGE_KEY = 'health:activeProfile';

// Phantom mode is persisted by storing 'phantom' as the active profile.
// We also remember the last real profile so we can restore it on exit.
const loadStoredPhantomPrev = (): string => {
    try {
        return localStorage.getItem('health:phantomPrevProfile') || DEFAULT_PROFILE;
    } catch {
        return DEFAULT_PROFILE;
    }
};

const saveStoredPhantomPrev = (profileId: string) => {
    try {
        localStorage.setItem('health:phantomPrevProfile', profileId);
    } catch { /* ignore */ }
};

export const useWeightData = (initialRange: DateRange = 'all') => {
    const [weightData, setWeightData] = useState<WeightDataPoint[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>(initialRange);
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);
    const [activeProfile, setActiveProfileState] = useState<string>(loadStoredProfile);
    const [settingProfile, setSettingProfile] = useState(false);
    const [scaleProfile, setScaleProfileState] = useState<string | null>(null);
    // Phantom mode: persisted via localStorage. Active when activeProfile === 'phantom'.
    const phantomMode = activeProfile === PHANTOM_PROFILE_ID;

    const togglePhantomMode = useCallback(async () => {
        if (!phantomMode) {
            // Entering phantom — save current real profile so we can restore it
            saveStoredPhantomPrev(activeProfile);
            saveStoredProfile(PHANTOM_PROFILE_ID);
            setActiveProfileState(PHANTOM_PROFILE_ID);
            setScaleProfileState(PHANTOM_PROFILE_ID);
        } else {
            // Leaving phantom — restore previous real profile
            const prev = loadStoredPhantomPrev();
            saveStoredProfile(prev);
            setActiveProfileState(prev);
            setScaleProfileState(prev);
        }
        const targetProfile = !phantomMode ? PHANTOM_PROFILE_ID : loadStoredPhantomPrev();
        try {
            await fetch(ACTIVE_PROFILE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${API_KEY}`,
                },
                body: JSON.stringify({ profileId: targetProfile }),
            });
        } catch {
            // non-fatal
        }
    }, [phantomMode, activeProfile]);

    // Switch profile: updates local view AND tells the Pi who's weighing in next.
    // The scale icon in the UI shows which profile is currently active for the Pi.
    const setActiveProfile = useCallback(async (profileId: string) => {
        setSettingProfile(true);
        saveStoredProfile(profileId);
        setActiveProfileState(profileId);
        try {
            const res = await fetch(ACTIVE_PROFILE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${API_KEY}`,
                },
                body: JSON.stringify({ profileId }),
            });
            if (res.ok) setScaleProfileState(profileId);
        } catch {
            // non-fatal
        } finally {
            setSettingProfile(false);
        }
    }, []);

    // On mount, fetch the active profile from the server so state syncs across
    // devices — e.g. enabling phantom on phone reflects on laptop and vice versa.
    useEffect(() => {
        fetch(ACTIVE_PROFILE_URL)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data?.profileId) {
                    setScaleProfileState(data.profileId);
                    // Sync active profile + localStorage from server state
                    setActiveProfileState(data.profileId);
                    saveStoredProfile(data.profileId);
                }
            })
            .catch(() => { /* non-fatal — falls back to localStorage */ });
    }, []);

    const fetchData = useCallback(async (range: DateRange, profile: string, opts?: { bypassCache?: boolean }) => {
        setError(null);

        const bypassCache = opts?.bypassCache === true;
        let cachedShown = false;

        if (!DEMO_MODE && !bypassCache) {
            const params = new URLSearchParams({
                metrics: 'weight',
                sort: 'asc',
                profileId: profile,
            });

            const filter = getDateRangeFilter(range);
            if (filter) {
                params.set('startDate', filter.startDate);
                params.set('endDate', filter.endDate);
            }

            const cacheKey = buildCacheKey(params);
            const cached = readCache(cacheKey);
            if (cached && Date.now() - cached.cachedAt <= CACHE_TTL_MS) {
                setWeightData(cached.data);
                setLastUpdated(
                    `Cached ${new Date(cached.cachedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                );
                cachedShown = true;
            }
        }

        if (!cachedShown) setLoading(true);

        try {
            if (DEMO_MODE) {
                const data = await fetchDemoData(range);
                setWeightData(data);
                setLastUpdated(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
                return;
            }

            const { data, cacheKey } = await fetchAPIData(range, profile);
            setWeightData(data);
            setLastUpdated(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            writeCache(cacheKey, { cachedAt: Date.now(), data });
        } catch (err) {
            console.error('Error fetching weight data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch weight data');
            if (!cachedShown) setWeightData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // In phantom mode we don't fetch or show any real data
        if (activeProfile === PHANTOM_PROFILE_ID) {
            setWeightData([]);
            setLastUpdated(null);
            setError(null);
            return;
        }
        // Clear stale lastUpdated from a previous profile before fetching
        setLastUpdated(null);
        fetchData(dateRange, activeProfile);
    }, [dateRange, activeProfile, fetchData]);

    return {
        weightData,
        loading,
        error,
        dateRange,
        setDateRange,
        lastUpdated,
        refetch: () => fetchData(dateRange, activeProfile, { bypassCache: true }),
        stats: calculateStats(weightData),
        isDemoMode: DEMO_MODE,
        activeProfile,
        setActiveProfile,
        settingProfile,
        // Which profile the Pi will route the next weigh-in to (shown via scale icon)
        scaleProfile,
        setScaleProfile: setActiveProfile, // alias — tapping pill sets both view + Pi routing
        settingScaleProfile: settingProfile,
        phantomMode,
        togglePhantomMode,
    };
};
